import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY = 'atlas-ide:v2:favorite-files';

export type FavoriteFile = {
  path: string;
  name: string;
  addedAt: number;
};

export function useFavoriteFiles() {
  const [favoriteFiles, setFavoriteFiles] = useState<FavoriteFile[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved) as FavoriteFile[];
      }
    } catch {
      // Ignore parse errors
    }
    return [];
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(favoriteFiles));
  }, [favoriteFiles]);

  // Check if a file is in favorites
  const isFavorite = useCallback((path: string): boolean => {
    return favoriteFiles.some((f) => f.path === path);
  }, [favoriteFiles]);

  // Add a file to favorites
  const addFavorite = useCallback((path: string) => {
    const name = path.split('/').pop() || path;
    setFavoriteFiles((prev) => {
      // Don't add if already exists
      if (prev.some((f) => f.path === path)) {
        return prev;
      }
      const newEntry: FavoriteFile = { path, name, addedAt: Date.now() };
      return [...prev, newEntry];
    });
  }, []);

  // Remove a file from favorites
  const removeFavorite = useCallback((path: string) => {
    setFavoriteFiles((prev) => prev.filter((f) => f.path !== path));
  }, []);

  // Toggle favorite status
  const toggleFavorite = useCallback((path: string) => {
    if (isFavorite(path)) {
      removeFavorite(path);
    } else {
      addFavorite(path);
    }
  }, [isFavorite, addFavorite, removeFavorite]);

  // Clear all favorites
  const clearFavorites = useCallback(() => {
    setFavoriteFiles([]);
  }, []);

  // Reorder favorites (drag & drop)
  const reorderFavorites = useCallback((fromIndex: number, toIndex: number) => {
    setFavoriteFiles((prev) => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return result;
    });
  }, []);

  return {
    favoriteFiles,
    isFavorite,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    clearFavorites,
    reorderFavorites,
  };
}
